import { formatDate, NgIf, DatePipe } from '@angular/common';
import { Component, computed, input, linkedSignal } from '@angular/core';
import { DiskCalculator } from 'app/pipes/disk-calculator';
import { LegendPosition, BarChartModule } from '@swimlane/ngx-charts';
import { LineChartSeries } from 'app/models/pie-chart-slice';
import { Selectable } from 'app/dropdown-module/models/selectable';
import { ZFDropdownComponent } from '../../dropdown-module/components/zf-dropdown.component';
import { DiskSizePipe } from '../../pipes/disk-size.pipe';

@Component({
    selector: 'zf-vertical-bar',
    templateUrl: './vertical-bar-widget.component.html',
    styleUrls: ['./vertical-bar-widget.component.scss'],
    imports: [NgIf, ZFDropdownComponent, BarChartModule, DatePipe, DiskSizePipe]
})
export class VerticalBarWidgetComponent {
    public readonly LegendPosition: typeof LegendPosition = LegendPosition;

    public readonly data = input<Array<LineChartSeries>>([]);

    // One entry per month present in the data, labelled with that month's total.
    public readonly days = computed<Array<Selectable>>((): Array<Selectable> => {
        const mapper: Map<string, number> = new Map();
        for (const k of this.data()) {
            // 2024-02-16T00:00:00+00:00
            const dd = this.fmtDate(k.name, 'yyyy-MM-01T00:00:00Z');
            mapper.set(dd, (mapper.get(dd) ?? 0) + k.series[0].value);
        }

        // format all the months together with the year
        const days: Array<Selectable> = [];
        for (const [key, val] of mapper) {
            const ddF = this.fmtDate(key, 'MMMM yyyy');
            const valF = this.formatDiskSize(val);
            days.push(new Selectable(`${ddF} (${valF})`, key));
        }
        return days;
    });

    // The selected month. Resets to the latest month whenever the data changes,
    // and is otherwise whatever the user picked.
    public readonly day = linkedSignal<string>((): string => this.days().last?.value);

    public readonly filterData = computed<Array<LineChartSeries>>((): Array<LineChartSeries> => {
        const day = this.day();
        if (!day) {
            return this.data();
        }
        return this.data().filter((a: LineChartSeries): boolean => {
            // force set to start of the month in the format
            const expect = this.fmtDate(a.name, 'yyyy-MM-01T00:00:00Z');
            return expect === day;
        });
    });

    public fmtDate(value: string | number, format: string = 'dd'): string {
        const fmtDate = formatDate(value, format, 'en-US', 'UTC');
        return fmtDate;
    }

    public formatDiskSize(value: number): string {
        return DiskCalculator.toHuman(value, 2);
    }

    public updateDay(event: string): void {
        this.day.set(event);
    }
}
